import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogSEO from '../components/BlogSEO';
import { getPostBySlug, getRelatedPosts, type BlogArticle } from '@/hooks/useBlogStore';
import { getArticleBySlug, getRelatedArticles, articles as mockArticles } from '@/mocks/blog';

function ArticleContent({ article }: { article: BlogArticle }) {
  return (
    <div className="max-w-3xl mx-auto">
      {/* Meta */}
      <div className="flex flex-wrap items-center gap-3 mb-6 text-sm font-label text-foreground-600">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 font-medium">
          {article.categoryLabel}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-calendar-line" />
          {new Date(article.date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-time-line" />
          {article.readTime}
        </span>
      </div>

      {/* Title */}
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6 leading-tight">
        {article.title}
      </h1>

      {/* Author */}
      <div className="flex items-center gap-3 mb-10 pb-8 border-b border-background-200/70">
        <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
          <i className="ri-user-line text-secondary-700" />
        </div>
        <div>
          <p className="font-label font-medium text-foreground-950">{article.author}</p>
          <p className="text-xs font-label text-foreground-500">ClearMind Editorial Team</p>
        </div>
      </div>

      {/* Hero Image */}
      <div className="mb-10 rounded-xl overflow-hidden">
        <img
          src={article.image}
          alt={article.imageAlt}
          className="w-full h-[300px] sm:h-[400px] object-cover object-top"
          loading="eager"
        />
      </div>

      {/* Article Body */}
      <article
        className="prose prose-lg max-w-none font-body text-foreground-700 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: article.content }}
      />

      {/* Tags */}
      <div className="mt-10 pt-8 border-t border-background-200/70">
        <div className="flex flex-wrap gap-2">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm font-label"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const fromAdmin = searchParams.get('ref') === 'admin';
  const [article, setArticle] = useState<BlogArticle | undefined>(() =>
    slug ? getArticleBySlug(slug) : undefined
  );
  const [related, setRelated] = useState<BlogArticle[]>([]);

  useEffect(() => {
    if (!slug) return;
    window.scrollTo(0, 0);
    let cancelled = false;
    const load = async () => {
      const dbArticle = await getPostBySlug(slug);
      const target = dbArticle || getArticleBySlug(slug);
      if (!target) return;
      if (!cancelled) setArticle(target);
      const rel = await getRelatedPosts(target);
      if (!cancelled) setRelated(rel.length > 0 ? rel : getRelatedArticles(target));
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-32 pb-20 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-search-line text-2xl text-foreground-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">
              Article Not Found
            </h1>
            <p className="text-foreground-600 mb-6 font-body">
              The article you are looking for does not exist or has been removed.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 font-label font-medium text-secondary-500 hover:text-secondary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <BlogSEO
        pageTitle={article.title}
        pageDescription={article.excerpt}
        canonicalPath={`/blog/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        isArticle
        publishedTime={article.date}
        modifiedTime={article.date}
        tags={article.tags}
        articleSchema={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          dateModified: article.date,
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: 'ClearMind Club' },
          mainEntityOfPage: `https://clearmind.club/blog/${article.slug}`,
        }}
      />
      <Navbar />
      <main className="pt-24 sm:pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {fromAdmin && (
            <div className="mb-6">
              <Link
                to="/blog/admin"
                className="inline-flex items-center gap-2 font-label text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors px-3 py-1.5 rounded-lg hover:bg-background-100"
              >
                <i className="ri-arrow-left-line" />
                Back to Blog Admin
              </Link>
            </div>
          )}
          <ArticleContent article={article} />

          {/* Related Articles */}
          {related.length > 0 && (
            <div className="mt-16 pt-12 border-t border-background-200/70">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-8">
                Related Articles
              </h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/blog/${rel.slug}`}
                    className="group flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/50 transition-colors"
                  >
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={rel.image}
                        alt={rel.imageAlt}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex flex-col justify-center">
                      <span className="text-xs font-label font-medium text-accent-700 mb-1">
                        {rel.categoryLabel}
                      </span>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {rel.title}
                      </h3>
                      <span className="text-xs font-label text-foreground-500 mt-1">
                        {rel.readTime}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}